import { ReactElement, useState, useEffect } from "react";
import { Navigate, Routes, Route } from "react-router-dom";

import { AuthProvider } from "./contexts/auth.context";
import { useAuth } from "./contexts/auth.context";
import { useProjectStore } from "./stores/project.store";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Logout from "./pages/Logout";
import ProjectPage from "./pages/Project";
import Header from "./components/Header.component";
//import Example from "./pages/Example";
import ProjectsSidebar from "./components/ProjectsSidebar.component";
import BasePaper from "./components/BasePaper.component";
import BaseModal from "./components/modals/BaseModal";
import ProjectSwitchConfirmationModal from "./components/modals/ProjectSwitchConfirmationModal";
import ProjectForm from "./components/forms/ProjectForm";
import type { Project } from "./services/projects.service";

const ProtectedApp = (): ReactElement => {
  const { auth, isAuthenticated } = useAuth();
  const {
    projects,
    selectedProject,
    fetchProjects,
    selectProject,
    refreshProjects,
    setActiveTab,
    hasStartedAnalysis,
  } = useProjectStore();
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showProjectSwitchModal, setShowProjectSwitchModal] = useState(false);
  const [pendingProject, setPendingProject] = useState<Project | null>(null);

  // Fetch projects when auth changes
  useEffect(() => {
    if (auth) {
      fetchProjects(auth);
    }
  }, [auth, fetchProjects]);

  const handleNewProject = () => {
    setShowCreateProjectModal(true);
  };

  const handleProjectSelect = (project: Project) => {
    // Check if there's a current project with active analysis
    if (selectedProject && selectedProject.id !== project.id) {
      // Only show confirmation modal if workflow has started
      if (hasStartedAnalysis) {
        setPendingProject(project);
        setShowProjectSwitchModal(true);
        return;
      }
    }

    // No current project, same project, or workflow hasn't started - switch directly
    selectProject(project);
    setActiveTab(project.project_name);
  };

  const handleConfirmProjectSwitch = () => {
    if (pendingProject) {
      selectProject(pendingProject);
      setActiveTab(pendingProject.project_name);
    }
    setShowProjectSwitchModal(false);
    setPendingProject(null);
  };

  const handleCancelProjectSwitch = () => {
    setShowProjectSwitchModal(false);
    setPendingProject(null);
  };

  const handleProjectCreated = async (newProject: any) => {
    if (newProject) {
      selectProject(newProject);
      setActiveTab(newProject.project_name);
      refreshProjects();
    }
    setShowCreateProjectModal(false);
  };

  const handleCloseModal = () => {
    setShowCreateProjectModal(false);
  };

  const handleProjectInfo = () => {
    // TODO: Implement project info functionality
  };

  const handleRefreshProject = () => {
    // TODO: Implement refresh project functionality
  };

  const handleEditProject = () => {
    // TODO: Implement edit project functionality
  };

  const handleDeleteProject = () => {
    // TODO: Implement delete project functionality
  };

  if (isAuthenticated) {
    return (
      <>
        <div className="flex h-[calc(100dvh-56px)] sm:h-[calc(100dvh-64px)] bg-bg overflow-hidden">
          <ProjectsSidebar
            selectedProjectId={selectedProject?.id}
            onProjectSelect={handleProjectSelect}
            onNewProject={handleNewProject}
            onProjectsChange={() => {}} // No longer needed with Zustand
            refreshTrigger={0} // No longer needed with Zustand
          />

          {selectedProject ? (
            <ProjectPage
              onInfo={handleProjectInfo}
              onRefresh={handleRefreshProject}
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
            />
          ) : (
            <BasePaper
              hasProjects={projects.length > 0}
              onNewProject={handleNewProject}
            />
          )}
        </div>

        {/* Create Project Modal */}
        <BaseModal
          open={showCreateProjectModal}
          onClose={handleCloseModal}
          title="Create New Project"
        >
          <ProjectForm
            mode="create"
            onSubmit={handleProjectCreated}
            onCancel={handleCloseModal}
          />
        </BaseModal>

        {/* Project Switch Confirmation Modal */}
        <ProjectSwitchConfirmationModal
          open={showProjectSwitchModal}
          currentProjectName={selectedProject?.project_name || ""}
          targetProjectName={pendingProject?.project_name || ""}
          hasAnalysisProgress={hasStartedAnalysis}
          onConfirm={handleConfirmProjectSwitch}
          onCancel={handleCancelProjectSwitch}
        />
      </>
    );
  }

  return <Home />;
};

const App = (): ReactElement => {
  return (
    <AuthProvider>
      <div id="root" className="h-dvh flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-hidden">
          <Routes>
            <Route path="/" element={<ProtectedApp />} />
            <Route path="/auth" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            {/* TODO: Remove this route before pushing to production */}
            {/* <Route path="/example" element={<Example />} />{" "} */}
          </Routes>
        </main>
      </div>
    </AuthProvider>
  );
};

export default App;
